import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from '@rails/activestorage'
import { buildRedactor } from '../redactor'
import Alpine from 'alpinejs'

const displayBytes = (size) => {
  if (size < 1024 * 1024) {
    return `${size >> 8} KB`
  } else if (size < 1024 * 1024 * 1024) {
    return `${size >> 16} MB`
  } else {
    return `${size >> 24} TB`
  }
}

const arrayUniq = (ary, { key } = { key: 'id' }) => {
  const aryHash = ary.reduce((rs, item) => {
    const itemKey = item[key]
    if (!rs[itemKey]) {
      rs[itemKey] = item
    }

    return rs
  }, {})

  return Object.values(aryHash)
}

export default class extends Controller {
  // 用意：去取得目前有的snippets，在`app/components/redactor/snippets`下
  static outlets = ['redactor--snippets']

  // 通常會設定
  //   data-redactor-direct-upload-url-value="<%= rails_direct_uploads_path %>"
  //   data-redactor-blob-url-template-value="<%= rails_service_blob_path(":signed_id", ":filename") %>"
  static values = {
    directUploadUrl: String,
    blobUrlTemplate: String,
    minHeight: { type: String, default: '25rem' },
    allowAddbarFormats: { type: Array, default: ['ai-tools', 'ai-image', 'text', 'heading', 'image', 'todo', 'list', 'embed', 'table', 'quote', 'pre', 'line', 'layout', 'wrapper'] },
    allowFormats: { type: Array, default: ['text', 'h1', 'h2', 'h3', 'h4', 'quote', 'bulletlist', 'numberedlist', 'todo'] },
    // NOTICE: false => 主要是讓sortable在sorting途中不會觸發disconnect
    // 參考: app/javascript/controllers/sortable_controller.js L28-L45
    destroyOnDisconnect: { type: Boolean, default: true },
    // NOTICE: false => 將redactor先disable，避免event觸發，產生奇怪的現象
    // 參考: app/javascript/controllers/sortable_controller.js L28-L45
    enabled: { type: Boolean, default: true }
  }

  connect() {
    this.editorApp = buildRedactor(this.element, {
      image: this.imageUploadSettings,
      filelink: this.fileUploadSettings,
      subscribe: {
        ...this.refreshSelectImageBoxCallbacks,
        ...this.refreshSelectFileBoxCallbacks
      },
      classname: 'prose-view',
      theme: 'light',
      minHeight: this.minHeightValue,
      popups: {
        addbar: this.allowAddbarFormatsValue,
        format: this.allowFormatsValue
      },
      snippets: this.redactorSnippetsOutlet.toJSON(),
    })
    this.addDataForImages()
    this.refreshDataStatesForImages()
    this.refreshDataStatesForFileLinks()
    this.watchUploadFinished()
  }

  disconnect() {
    if (!this.destroyOnDisconnectValue) { return }

    try {
      this.editorApp.destroy()
    } catch (e) {
      console.warn(e)
    }
  }

  get store() {
    this._store ||= Alpine.reactive({
      uploadStates: { status: 'init', result: {} },
      uploadApp: null,
    })
    return this._store
  }

  get uploadStates() {
    return this.store.uploadStates
  }

  get imageUploadSettings() {
    const callbacks = {
      // MUST: because `DirectUpload` will use
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener('progress', ({ loaded, total }) => {
          console.log(Math.floor(loaded * 100.0 / total))
        })
      }
    }

    return {
      upload: (uploadApp, data) => {
        this.store.uploadApp = uploadApp
        this.resetUploadStates()

        for (let key in data.files) {
          if (typeof data.files[key] === 'object') {
            this.uploadStates.result[key] = { progress: 0, id: null, url: '' }

            const instance = new DirectUpload(data.files[key], this.directUploadUrlValue, callbacks)
            instance.create((error, blob) => {
              if (error) {
                this.uploadStates.result[key].progress = 100
                console.error(error)
              } else {
                // Add an appropriately-named hidden input to the form
                // with a value of blob.signed_id
                const blobUrl = this.blobUrlTemplateValue.replace(':signed_id', blob.signed_id)
                                                         .replace(':filename', data.files[key].name)
                this.uploadStates.result[key] = { progress: 100, id: blob.signed_id, url: blobUrl }
              }
            })
          }
        }
      }
    }
  }

  get fileUploadSettings() {
    const callbacks = {
      // MUST: because `DirectUpload` will use
      directUploadWillStoreFileWithXHR(request) {
        request.upload.addEventListener('progress', ({ loaded, total }) => {
          console.log(Math.floor(loaded * 100.0 / total))
        })
      }
    }

    return {
      upload: (uploadApp, data) => {
        this.store.uploadApp = uploadApp
        this.resetUploadStates()

        for (let key in data.files) {
          if (typeof data.files[key] === 'object') {
            this.uploadStates.result[key] = { progress: 0, id: null, url: '' }

            const instance = new DirectUpload(data.files[key], this.directUploadUrlValue, callbacks)
            instance.create((error, blob) => {
              if (error) {
                this.uploadStates.result[key].progress = 100
                console.error(error)
              } else {
                // Add an appropriately-named hidden input to the form
                // with a value of blob.signed_id
                const blobUrl = this.blobUrlTemplateValue.replace(':signed_id', blob.signed_id)
                                                         .replace(':filename', data.files[key].name)
                this.uploadStates.result[key] = {
                  progress: 100,
                  id: blob.signed_id,
                  url: blobUrl,
                  name: data.files[key].name,
                  size: displayBytes(data.files[key].size)
                }
              }
            })
          }
        }
      }
    }
  }

  get refreshSelectImageBoxCallbacks() {
    const callback = () => {
      // const images = Object.values(this.editorApp.image.dataStates)
      // const unSelectedImages = images.filter( image => !image.status )
      //                                .reduce((rs, image) => ({...rs, [image.id]: image}), {})
      const unSelectedImages = this.editorApp.image.dataStates
      this.editorApp.opts.opts.image.select = unSelectedImages
    }

    return {
      'modal.before.open': callback,
      'image.select': callback,
      'image.upload': callback,
      'image.remove': callback,
    }
  }

  get refreshSelectFileBoxCallbacks() {
    const callback = ({ params: { response } }) => {
      const filelinks = Object.values(response).map(({name, url, size}) => ({ name, url, size }))
      const filelinkInEditor = this.editorApp.opts.opts.filelink
      filelinkInEditor.select = arrayUniq(filelinkInEditor.select.concat(filelinks), { key: 'url' })
    }

    return {
      'file.upload': callback,
    }
  }

  // private

  resetUploadStates() {
    this.store.uploadStates = {
      status: 'init',
      result: {},
    }
  }

  // 把editor裡面的image補上[data-image]
  addDataForImages() {
    let utils = this.editorApp.create('utils')
    this.editorApp.editor.getLayout().find('img').each($node => {
      if ( $node.attr('data-image') ) { return }

      $node.attr('data-image', utils.getRandomId())
    })
  }

  // 把圖片狀態更新後，就能在點擊`image button`的modal後，可以選擇已經存在editor裡面的image
  refreshDataStatesForImages() {
    this.editorApp.image.observeStates()
    this.editorApp.opts.opts.image.select = this.editorApp.image.dataStates
  }

  // 把editor裡面的filelink找出來，加入可選擇的filelink列表裡
  refreshDataStatesForFileLinks() {
    const filelinks = this.editorApp.editor.getLayout().find('a').nodes
                          .filter($node => $node.hasAttribute('data-file'))
                          .map($node => ({
                            name: $node.getAttribute('data-name'),
                            url: $node.getAttribute('href'),
                            size: $node.getAttribute('data-size')
                          }))

    this.editorApp.opts.opts.filelink.select = arrayUniq(filelinks, { key: 'url' })
  }

  watchUploadFinished() {
    Alpine.watch(
      () => Object.values(this.uploadStates.result).every(rs => rs.progress === 100),
      (isFinished, prevState) => {
        if (!isFinished || isFinished === prevState) { return }

        this.uploadStates.status = 'finished'
      }
    )

    Alpine.watch(
      () => this.uploadStates.status,
      (status) => {
        if (status !== 'finished' || !this.store.uploadApp) { return }

        this.store.uploadApp.complete(this.uploadStates.result)
      }
    )
  }

  enabledValueChanged(newValue) {
    if (newValue) {
      this.editorApp?.enable()
    } else {
      this.editorApp?.disable()
    }
  }
}
